import React, { useEffect } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Form, Icon } from 'semantic-ui-react'
import { FormFields } from '~/components/FormFields'
import { FarmerCreateOptionsType } from '~/api/services/farmers'
import { StyledFormSection } from '../style'

interface Props {
  farmerOptions: FarmerCreateOptionsType
}

const Production: React.FC<Props> = ({ farmerOptions }) => {
  const { t } = useTranslation()
  const { control, setValue } = useFormContext()

  const watchMilkTypeId = useWatch({ control, name: 'milkTypeId' })

  const milkTypeOption = farmerOptions?.milkTypes?.find(milkType => milkType.id === watchMilkTypeId)
  const showOrganicCertification = Boolean(milkTypeOption?.isBio)

  useEffect(() => {
    if (!showOrganicCertification) {
      setValue('organicCertificationNumber', '')
    }
  }, [watchMilkTypeId])

  return (
    <StyledFormSection>
      <div className='section-header'>
        <Icon name='factory' />
        <span className='section-title'>{t('Production')}</span>
      </div>
      <div className='section-content'>
        <Form.Group widths={'equal'}>
          <FormFields.Select
            selectOnBlur={false}
            control={control}
            label={t('Milk Type')}
            name={'milkTypeId'}
            clearable
            placeholder={t('Select milk type')}
            options={farmerOptions?.milkTypes?.map(milkType => ({
              value: milkType?.id,
              text: milkType?.name
            })) || []}
          />
          {showOrganicCertification && (
            <FormFields.Input
              control={control}
              name={'organicCertificationNumber'}
              placeholder={t('Organic Certification Number')}
              label={t('Organic Certification #')}
              noErrorMessage
              rules={{
                required: true
              }}
            />
          )}
          <FormFields.Select
            name={'milkTypeCharacteristicId'}
            selectOnBlur={false}
            control={control}
            label={t('Milk Characteristics')}
            clearable
            placeholder={t('Select milk characteristics')}
            options={farmerOptions?.milkTypeCharacteristics?.map(milkType => ({
              value: milkType?.id,
              text: milkType?.label
            })) || []}
          />
          <FormFields.Select
            name={'milkPurchase'}
            selectOnBlur={false}
            control={control}
            label={t('Milk Purchase')}
            clearable
            placeholder={t('Select milk purchase')}
            options={farmerOptions?.milkPurchases?.map(milkPurchase => ({
              value: milkPurchase?.key,
              text: milkPurchase?.label
            })) || []}
          />
        </Form.Group>

        <Form.Group widths={'equal'}>
          <FormFields.Select
            control={control}
            rules={{
              required: t('Please select production zone')
            }}
            placeholder={t('Please select production zone')}
            label={t('Production Zone')}
            search
            name={'productionZoneId'}
            options={farmerOptions?.productionZones?.map(productionZone => ({
              value: productionZone.id,
              text: productionZone.name
            })) || []}
          />
          <FormFields.Select
            control={control}
            rules={{
              required: t('Please select operating form')
            }}
            search
            placeholder={t('Please select operating form')}
            label={t('Operating Form')}
            name={'operatingFormId'}
            options={farmerOptions?.operatingForms?.map(operatingForm => ({
              value: operatingForm.id,
              text: operatingForm.name
            })) || []}
          />
        </Form.Group>
      </div>
    </StyledFormSection>
  )
}

export default Production
